// @flow

import 'isomorphic-fetch'
import { push } from 'react-router-redux'
import {
  checkHttpStatus,
  getApiHeaders,
  getApiBaseUrlWithOrg,
  dataUriToBlob
} from '../utils'
import { basicGet } from '../utils/api'
import { messages as alertMessages } from '../utils/messages/alert'
import {
  fetchQuestComments,
  fetchQuestActivityComments,
  fetchUserQuestComments,
  fetchUserActivityComments
} from './comments'
import { getErrorAction } from '../utils/actions'
import { fetchCurrentCompiledQuest } from './review'
import { fetchQuestTopics } from './topics'
import type { Action } from '../types/actions/quest'
import type { APIError } from '../types'

export const REQUEST_QUEST = 'REQUEST_QUEST'
export const RECEIVE_QUEST = 'RECEIVE_QUEST'
export const FETCH_QUEST_FAILURE = 'FETCH_QUEST_FAILURE'
export const REQUEST_QUEST_NOTES = 'REQUEST_QUEST_NOTES'
export const RECEIVE_QUEST_NOTES = 'RECEIVE_QUEST_NOTES'
export const FAILED_QUEST_NOTES = 'FAILED_QUEST_NOTES'
export const SET_QUEST_SECTION = 'SET_QUEST_SECTION'
export const DELETE_QUEST_SUCCESS = 'DELETE_QUEST_SUCCESS'
export const EDIT_QUEST_DETAILS_SUCCESS = 'EDIT_QUEST_DETAILS_SUCCESS'
export const EDIT_QUEST_MENTOR_NOTE_SUCCESS = 'EDIT_QUEST_MENTOR_NOTE_SUCCESS'
export const DELETE_NOTE_SUCCESS = 'DELETE_NOTE_SUCCESS'
export const CREATE_QUEST_MENTOR_NOTE_SUCCESS =
  'CREATE_QUEST_MENTOR_NOTE_SUCCESS'
export const CREATE_MENTOR_NOTE = 'CREATE_MENTOR_NOTE'
export const START_EDIT_QUEST = 'START_EDIT_QUEST'
export const CANCEL_EDIT_QUEST = 'CANCEL_EDIT_QUEST'
export const DELETE_NOTE_REQUEST = 'DELETE_NOTE_REQUEST'
export const CREATE_QUEST_SUCCESS = 'CREATE_QUEST_SUCCESS'
export const CREATE_QUEST_REQUEST = 'CREATE_QUEST_REQUEST'
export const CREATE_QUEST_FAILURE = 'CREATE_QUEST_FAILURE'
export const ADD_EDITOR_TO_QUEST = 'ADD_EDITOR_TO_QUEST'
export const REQUEST_QUEST_READABLE_STATS = 'REQUEST_QUEST_READABLE_STATS'
export const RECEIVE_QUEST_READABLE_STATS = 'RECEIVE_QUEST_READABLE_STATS'
export const FAILED_QUEST_READABLE_STATS = 'FAILED_QUEST_READABLE_STATS'
export const EDIT_QUEST_DETAILS_FAILURE = 'EDIT_QUEST_DETAILS_FAILURE'
export const REQUEST_QUEST_ANALYTICS = 'REQUEST_QUEST_ANALYTICS'
export const RECEIVE_QUEST_ANALYTICS = 'RECEIVE_QUEST_ANALYTICS'
export const FAILED_QUEST_ANALYTICS = 'FAILED_QUEST_ANALYTICS'
export const EDIT_QUEST_DETAILS_REQUEST = 'EDIT_QUEST_DETAILS_REQUEST'
export const FETCH_QUEST_HISTORY_REQUEST = 'FETCH_QUEST_HISTORY_REQUEST'
export const FETCH_QUEST_HISTORY_SUCCESS = 'FETCH_QUEST_HISTORY_SUCCESS'
export const CLEAR_QUEST_HISTORY = 'CLEAR_QUEST_HISTORY'
export const FETCH_QUEST_ACTIVITIES_REQUEST = 'FETCH_QUEST_ACTIVITIES_REQUEST'
export const FETCH_QUEST_ACTIVITIES_SUCCESS = 'FETCH_QUEST_ACTIVITIES_SUCCESS'
export const FETCH_IN_USE_COMPILED_QUESTS_REQUEST =
  'FETCH_IN_USE_COMPILED_QUESTS_REQUEST'
export const FETCH_IN_USE_COMPILED_QUESTS_SUCCESS =
  'FETCH_IN_USE_COMPILED_QUESTS_SUCCESS'
export const FETCH_CURRENT_ASSIGNMENT_REQUEST =
  'FETCH_CURRENT_ASSIGNMENT_REQUEST'
export const FETCH_CURRENT_ASSIGNMENT_SUCCESS =
  'FETCH_CURRENT_ASSIGNMENT_SUCCESS'
export const FETCH_QUEST_APPROVALS_REQUEST = 'FETCH_QUEST_APPROVALS_REQUEST'
export const FETCH_QUEST_APPROVALS_SUCCESS = 'FETCH_QUEST_APPROVALS_SUCCESS'
export const FETCH_QUEST_GRADE_LEVELS_REQUEST =
  'FETCH_QUEST_GRADE_LEVELS_REQUEST'
export const FETCH_QUEST_GRADE_LEVELS_SUCCESS =
  'FETCH_QUEST_GRADE_LEVELS_SUCCESS'
export const START_FETCHING_QUEST_DATA = 'START_FETCHING_QUEST_DATA'
export const END_FETCHING_QUEST_DATA = 'END_FETCHING_QUEST_DATA'
export const UPDATE_QUEST_IMAGE_PROPERTIES_SUCCESS =
  'UPDATE_QUEST_IMAGE_PROPERTIES_SUCCESS'

export function editQuestDetailsRequest() {
  return {
    type: EDIT_QUEST_DETAILS_REQUEST
  }
}

export function addEditorToQuest(user: Object): Action {
  return {
    type: ADD_EDITOR_TO_QUEST,
    payload: {
      user: user
    }
  }
}

export function createQuestFailure(error: APIError): Action {
  return getErrorAction(CREATE_QUEST_FAILURE, error)
}

export function createQuestRequest(): Action {
  return {
    type: CREATE_QUEST_REQUEST
  }
}

export function createQuestSuccess(response: Object): Action {
  return {
    type: CREATE_QUEST_SUCCESS,
    quest: response
  }
}

export function deleteNoteRequest(): Action {
  return {
    type: DELETE_NOTE_REQUEST
  }
}

export function startEditQuest(): Action {
  return {
    type: START_EDIT_QUEST
  }
}

export function cancelEditQuest(): Action {
  return {
    type: CANCEL_EDIT_QUEST
  }
}

export function createMentorNote(): Action {
  return {
    type: CREATE_MENTOR_NOTE
  }
}

export function deleteNoteAction(noteId: number): Action {
  return {
    type: DELETE_NOTE_SUCCESS,
    noteId: noteId
  }
}

export function deleteQuestSuccess(questId: number): Action {
  return {
    type: DELETE_QUEST_SUCCESS,
    questId: questId
  }
}

export function requestQuest(questId): Action {
  return {
    type: REQUEST_QUEST,
    questId: questId
  }
}

export function receiveQuest(response: Object): Action {
  return {
    type: RECEIVE_QUEST,
    quest: response
  }
}

export function requestQuestNotes(): Action {
  return {
    type: REQUEST_QUEST_NOTES
  }
}

export function receiveQuestNotes(response: Object): Action {
  return {
    type: RECEIVE_QUEST_NOTES,
    notes: response.quest_notes
  }
}

export function setQuestSection(
  section: string,
  activityId: ?number,
  subsection: ?string
): Action {
  return {
    type: SET_QUEST_SECTION,
    section: section,
    subsection: subsection,
    activityId: activityId || null
  }
}

export function editQuestDetailsSuccess(response: Object): Action {
  return {
    type: EDIT_QUEST_DETAILS_SUCCESS,
    quest: response
  }
}

export function editQuestMentorNoteSuccess(
  questId: number,
  response: Object
): Action {
  return {
    type: EDIT_QUEST_MENTOR_NOTE_SUCCESS,
    payload: {
      questId: questId,
      note: response
    }
  }
}

export function createQuestMentorNoteSuccess(response: Object): Action {
  return {
    type: CREATE_QUEST_MENTOR_NOTE_SUCCESS,
    note: response
  }
}

export function failedQuestNotes(error: APIError): Action {
  return getErrorAction(FAILED_QUEST_NOTES, error)
}

export function fetchQuestFailure(error: APIError): Action {
  return getErrorAction(FETCH_QUEST_FAILURE, error)
}

export function requestQuestReadableStats() {
  return {
    type: REQUEST_QUEST_READABLE_STATS
  }
}

export function receiveQuestReadableStats(response) {
  return {
    type: RECEIVE_QUEST_READABLE_STATS,
    payload: {
      stats: response.readable_stats
    }
  }
}

export function failedQuestReadableStats(error) {
  return getErrorAction(FAILED_QUEST_READABLE_STATS, error)
}

export function editQuestDetailsFailure(error) {
  return {
    type: EDIT_QUEST_DETAILS_FAILURE,
    payload: {
      error
    }
  }
}

export function requestQuestAnalytics() {
  return {
    type: REQUEST_QUEST_ANALYTICS
  }
}

export function receiveQuestAnalytics(response) {
  return {
    type: RECEIVE_QUEST_ANALYTICS,
    payload: {
      quest_analytics: response
    }
  }
}

export function failedQuestAnalytics(error) {
  return getErrorAction(FAILED_QUEST_ANALYTICS, error)
}

export function fetchQuestHistoryRequest(reload) {
  return {
    type: FETCH_QUEST_HISTORY_REQUEST,
    payload: {
      reload
    }
  }
}

export function fetchQuestHistorySuccess(history) {
  return {
    type: FETCH_QUEST_HISTORY_SUCCESS,
    payload: {
      quest_events: history.quest_events,
      page: history.meta.page,
      total: history.meta.total
    }
  }
}

export function clearQuestHistory() {
  return {
    type: CLEAR_QUEST_HISTORY
  }
}

export function fetchQuestActivitiesRequest() {
  return {
    type: FETCH_QUEST_ACTIVITIES_REQUEST
  }
}

export function fetchQuestActivitiesSuccess(response) {
  return {
    type: FETCH_QUEST_ACTIVITIES_SUCCESS,
    payload: {
      activities: response.activities
    }
  }
}

export function fetchInUseCompiledQuestsRequest() {
  return {
    type: FETCH_IN_USE_COMPILED_QUESTS_REQUEST
  }
}

export function fetchInUseCompiledQuestsSuccess(inUseCompiledQuests) {
  return {
    type: FETCH_IN_USE_COMPILED_QUESTS_SUCCESS,
    payload: {
      inUseCompiledQuests
    }
  }
}

export function fetchCurrentAssignmentRequest() {
  return {
    type: FETCH_CURRENT_ASSIGNMENT_REQUEST
  }
}

export function fetchCurrentAssignmentSuccess(assignment) {
  return {
    type: FETCH_CURRENT_ASSIGNMENT_SUCCESS,
    payload: {
      assignment
    }
  }
}

export function fetchQuestApprovalsRequest() {
  return {
    type: FETCH_QUEST_APPROVALS_REQUEST
  }
}

export function fetchQuestApprovalsSuccess(approvals) {
  return {
    type: FETCH_QUEST_APPROVALS_SUCCESS,
    payload: {
      approvals
    }
  }
}

export function fetchQuestGradeLevelsRequest() {
  return {
    type: FETCH_QUEST_GRADE_LEVELS_REQUEST
  }
}

export function fetchQuestGradeLevelsSuccess(gradeLevels) {
  return {
    type: FETCH_QUEST_GRADE_LEVELS_SUCCESS,
    payload: {
      gradeLevels
    }
  }
}

export function startFetchingQuestData() {
  return {
    type: START_FETCHING_QUEST_DATA
  }
}

export function endFetchingQuestData() {
  return {
    type: END_FETCHING_QUEST_DATA
  }
}

export function updateQuestImagePropertiesSuccess(imageProperties) {
  return {
    type: UPDATE_QUEST_IMAGE_PROPERTIES_SUCCESS,
    payload: {
      imageProperties
    }
  }
}

export function deleteQuest(questId: number): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}`
    try {
      checkHttpStatus(
        await fetch(url, {
          method: 'DELETE',
          headers: {
            Authorization: `Token token="${state.user.token}"`,
            Accept: 'application/json, application/vnd.oefngl.v1',
            'Content-Type': 'application/json'
          }
        })
      )
      dispatch(deleteQuestSuccess(questId))
    } catch (error) {
      console.log(error)
    }
  }
}

export function fetchQuestNotes(id: number, state: Object): Function {
  return dispatch => {
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${id}/notes`
    return basicGet(
      state,
      dispatch,
      url,
      requestQuestNotes,
      receiveQuestNotes,
      failedQuestNotes
    )
  }
}

export function fetchQuest(id: number): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(state.organization.orgId)}/quests/` + id
    dispatch(startFetchingQuestData())
    dispatch(requestQuest(id))
    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'GET',
          headers: getApiHeaders(state)
        })
      )
      const quest = await response.json()
      await dispatch(receiveQuest(quest))
      await dispatch(fetchQuestActivities())
      await dispatch(fetchQuestApprovals())
      await dispatch(fetchCurrentAssignment())
      await dispatch(fetchQuestGradeLevels())
      await dispatch(fetchQuestComments(id, state))
      await dispatch(fetchQuestActivityComments(id, state))
      await dispatch(fetchUserQuestComments(id, state))
      await dispatch(fetchUserActivityComments(id, state))
      await dispatch(fetchQuestNotes(id, state))
      await dispatch(fetchQuestTopics(id, state))
      await dispatch(fetchQuestAnalytics(id))
      await dispatch(fetchInUseCompiledQuests())
      dispatch(endFetchingQuestData())
    } catch (error) {
      dispatch(fetchQuestFailure(error))
      if (error.response.status === 404) {
        dispatch(push('/notfound'))
      }
    }
  }
}

export function editQuestDetails(quest: Object): Function {
  return (dispatch, getState) => {
    const state = getState()

    // need to trim whitespace from quest name on update request
    quest.name = quest.name.trim()

    const url = `${getApiBaseUrlWithOrg(state.organization.orgId)}/quests/${
      quest.id
    }`
    let section = ''
    if (state.quest.section === 'basicinformation') {
      section = 'two'
    } else if (state.quest.section === 'otherinformation') {
      section = 'three'
    }
    dispatch(editQuestDetailsRequest())

    return fetch(url, {
      method: 'PUT',
      headers: getApiHeaders(state),
      body: JSON.stringify({
        quest: quest,
        section
      })
    })
      .then(response => {
        if (!response.ok) {
          throw response
        }
        return response.json() // we only get here if there is no error
      })
      .then(json => {
        dispatch(editQuestDetailsSuccess(json))
        dispatch(addEditorToQuest(state.user.user))
      })
      .catch(err => {
        err.text().then(errorMessage => {
          dispatch(editQuestDetailsFailure(errorMessage))
        })
      })
  }
}

export function editQuestMentorNote(
  id: number,
  note: Object,
  noteId: number
): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${id}/notes/${noteId}`
    let data = new window.FormData()
    data.append('note[description]', note.description || '')

    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'PUT',
          headers: {
            Authorization: `Token token="${state.user.token}"`,
            Accept: 'application/vnd.oefngl.v3'
          },
          body: data
        })
      )
      const json = await response.json()
      dispatch(editQuestMentorNoteSuccess(id, json))
      dispatch(addEditorToQuest(state.user.user))
    } catch (error) {
      console.log(error)
    }
  }
}

export function createQuestMentorNote(questId: number, note: Object): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/notes`
    let data = new FormData()
    data.append('note[attachment]', note.file)
    data.append('note[description]', note.description || '')

    dispatch(createMentorNote())
    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'POST',
          headers: {
            Authorization: `Token token="${state.user.token}"`,
            Accept: 'application/vnd.oefngl.v3'
          },
          body: data
        })
      )
      const json = await response.json()
      dispatch(createQuestMentorNoteSuccess(json))
      dispatch(addEditorToQuest(state.user.user))
    } catch (error) {
      console.log(error)
    }
  }
}

export function deleteNote(questId: number, noteId: number): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/notes/${noteId}`
    dispatch(deleteNoteRequest())
    try {
      checkHttpStatus(
        await fetch(url, {
          method: 'DELETE',
          headers: getApiHeaders(state)
        })
      )
      dispatch(deleteNoteAction(noteId))
      dispatch(addEditorToQuest(state.user.user))
    } catch (error) {
      console.log(error)
    }
  }
}

export function createQuest(questName: string, intl: Object): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(state.organization.orgId)}/quests`
    dispatch(createQuestRequest())
    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'POST',
          headers: getApiHeaders(state),
          body: JSON.stringify({
            name: questName
          })
        })
      )
      const json = await response.json()
      dispatch(createQuestSuccess(json))
      dispatch(setQuestSection('basicinformation'))
      dispatch(push(`/quest/${json.id}/basicinformation`))
    } catch (error) {
      dispatch(createQuestFailure(error))
      if (error.response.status === 422) {
        window.alert(intl.formatMessage(alertMessages.questTitleTaken))
      } else {
        window.alert('There was an error trying to add this Quest.')
      }
    }
  }
}

export function fetchQuestReadableStats(questId) {
  return (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/readable_stats`
    return basicGet(
      state,
      dispatch,
      url,
      requestQuestReadableStats,
      receiveQuestReadableStats,
      failedQuestReadableStats
    )
  }
}

export function fetchQuestAnalytics(questId) {
  return (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/analytics`
    return basicGet(
      state,
      dispatch,
      url,
      requestQuestAnalytics,
      receiveQuestAnalytics,
      failedQuestAnalytics
    )
  }
}

export function fetchQuestHistory(page, reload) {
  return async (dispatch, getState) => {
    const state = getState()
    const questId = state.quest.item.id
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/quest_events?page=${page}`
    dispatch(fetchQuestHistoryRequest(reload))
    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'GET',
          headers: getApiHeaders(state)
        })
      )
      const json = await response.json()
      dispatch(fetchQuestHistorySuccess(json))
    } catch (e) {
      console.log(e)
    }
  }
}

export function fetchQuestActivities() {
  return (dispatch, getState) => {
    const state = getState()
    const questId = state.quest.item.id
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/activities`
    return basicGet(
      state,
      dispatch,
      url,
      fetchQuestActivitiesRequest,
      fetchQuestActivitiesSuccess
    )
  }
}

export function fetchInUseCompiledQuests() {
  return async (dispatch, getState) => {
    const state = getState()
    const questId = state.quest.item.id
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/in_use_compiled_quests`
    dispatch(fetchInUseCompiledQuestsRequest())
    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'GET',
          headers: getApiHeaders(state)
        })
      )
      const json = await response.json()
      await dispatch(fetchInUseCompiledQuestsSuccess(json))
      if (json.current_compiled_quest) {
        await dispatch(
          fetchCurrentCompiledQuest(questId, json.current_compiled_quest.id)
        )
      }
    } catch (e) {
      console.log(e)
    }
  }
}

export function fetchCurrentAssignment() {
  return (dispatch, getState) => {
    const state = getState()
    const questId = state.quest.item.id
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/assignments/current_assignment`
    return basicGet(
      state,
      dispatch,
      url,
      fetchCurrentAssignmentRequest,
      fetchCurrentAssignmentSuccess
    )
  }
}

export function fetchQuestApprovals() {
  return (dispatch, getState) => {
    const state = getState()
    const questId = state.quest.item.id
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/approvals`
    return basicGet(
      state,
      dispatch,
      url,
      fetchQuestApprovalsRequest,
      fetchQuestApprovalsSuccess
    )
  }
}

export function fetchQuestGradeLevels() {
  return (dispatch, getState) => {
    const state = getState()
    const questId = state.quest.item.id
    const url = `${getApiBaseUrlWithOrg(
      state.organization.orgId
    )}/quests/${questId}/grade_levels`
    return basicGet(
      state,
      dispatch,
      url,
      fetchQuestGradeLevelsRequest,
      fetchQuestGradeLevelsSuccess
    )
  }
}

export function removeIconImage(): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(state.organization.orgId)}/quests/${
      state.quest.item.id
    }/update_icon_image`
    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'PATCH',
          headers: getApiHeaders(state),
          body: JSON.stringify({
            quest: {
              remove_icon_image: true,
              icon_image_license_id: null,
              icon_image_license_properties: {}
            }
          })
        })
      )
      const json = await response.json()
      dispatch(updateQuestImagePropertiesSuccess(json))
      dispatch(addEditorToQuest(state.user.user))
    } catch (error) {
      console.log(error)
    }
  }
}

export function updateQuestImageSource(iconImageSource: string): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(state.organization.orgId)}/quests/${
      state.quest.item.id
    }/update_icon_image`
    dispatch(editQuestDetailsRequest())
    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'PATCH',
          headers: getApiHeaders(state),
          body: JSON.stringify({
            quest: {
              icon_image_license_properties: {
                source_url: iconImageSource
              }
            }
          })
        })
      )
      const json = await response.json()
      dispatch(updateQuestImagePropertiesSuccess(json))
      dispatch(addEditorToQuest(state.user.user))
    } catch (error) {
      console.log(error)
    }
  }
}

export function uploadImage(
  type: string,
  dataUri: string,
  fileName: string
): Function {
  return async (dispatch, getState) => {
    const state = getState()
    const url = `${getApiBaseUrlWithOrg(state.organization.orgId)}/quests/${
      state.quest.item.id
    }/update_icon_image`
    let data = new FormData()
    data.append('quest[' + type + '_image]', dataUriToBlob(dataUri), fileName)

    try {
      const response = checkHttpStatus(
        await fetch(url, {
          method: 'PATCH',
          headers: {
            Authorization: `Token token="${state.user.token}"`,
            Accept: 'application/vnd.oefngl.v3'
          },
          body: data
        })
      )
      const json = await response.json()
      dispatch(updateQuestImagePropertiesSuccess(json))
      dispatch(addEditorToQuest(state.user.user))
    } catch (error) {
      console.log(error)
    }
  }
}



// WEBPACK FOOTER //
// ./src/actions/quest.js